/**
 * groupList.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
 */

import bluebird from "bluebird";
import * as React from "react";

import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import GroupsIcon from "@mui/icons-material/Groups";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import LogoutIcon from "@mui/icons-material/Logout";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
//import PhonelinkEraseIcon from '@mui/icons-material/PhonelinkErase';
import { ClickAwayListener, Divider, IconButton, ListItemIcon, ListItemText, 
  Paper, Popper, MenuItem, MenuList, Tooltip, Typography
 } from "@mui/material";

import { AuthContext } from "../auth/auth.context";
import { FailedRecoveryDialog } from "../groups/failedRecoveryModal.component";
import { GroupNavContext } from "../chat/groupNav.context";
import { ErrorSnackBar } from "../components/errorSnackBar.component";

import { ErrorCode, GroupError, RecoveryTooOldError } from "../models/error";
import { GroupMembership, GroupType } from "../models/group";

import { groupServiceFactory } from "../services/group.service";
import { PermissionLevel } from "../models/auth";

interface GroupListContainerProps {
  currentGroup: GroupMembership | null,
  groups: Array<GroupMembership>,
  directMessages: Array<GroupMembership>,
  unreadThreadMessages: number,
  unreadThreadMentions: number,
  onDoAddGroup(): void,
  onOpenEditMeetingDialog(): void,
  selectedGroup(group: GroupMembership): void
}
enum Section {
  headers,
  groups,
  directMessages
}
export function GroupListContainer(props: GroupListContainerProps): React.ReactElement<any, any> {
  const [openGroupMenu, setOpenGroupMenu] = React.useState<boolean>(false);
  const [openBrowseMenu, setOpenBrowseMenu] = React.useState<boolean>(false);
  const [openFailedRecovery, setOpenFailedRecovery] = React.useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const browseMenuAnchorRef = React.useRef<HTMLButtonElement>(null);
  const [errMsg, setErrMsg] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [selectedGroup, setSelectedGroup] = React.useState<null | GroupMembership>(null);
  const [selectedData, setSelectedData] = React.useState<{ index: number, section: Section }>({ index: -1, section: Section.groups });
  const [hoverData, setHoverData] = React.useState<{ index: number, section: Section }>({ index: -1, section: Section.groups });
  const authContext = React.useContext<AuthContext>(AuthContext);
  const navContext = React.useContext<GroupNavContext>(GroupNavContext);

  function groupClick(event: React.SyntheticEvent, groupmember: GroupMembership, index: number, section: Section) {
    event.preventDefault();
    event.stopPropagation();
    props.selectedGroup(groupmember);
    setSelectedData({ index, section });
    setOpenGroupMenu(false);
  }

  function threadsClick(event: React.SyntheticEvent) {
    event.preventDefault();
    event.stopPropagation();
    navContext.navTo("/group/threads");
    navContext.setCurrentGroup(null);
    setSelectedData({ index: 1, section: Section.headers });
    setOpenGroupMenu(false);
  }

  function toggleHuddle(groupID: string) {
    if (navContext.huddleGroup) {
      navContext.leaveHuddle();
    } else {
      navContext.doHuddle(groupID);
    }
  }

  function checkAnyUnread() {
    return !!props.groups.find(membership => {
      return membership.unreadMentions || membership.unreadMessages
    })
  }

  function doOpenMenu(event: React.MouseEvent<HTMLButtonElement>,
    groupmember: GroupMembership, index: number, section: Section) {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setSelectedGroup(groupmember);
    if (openGroupMenu) {
      setSelectedData({ index: -1, section: Section.groups });
    } else {
      setSelectedData({ index, section: section });
    }
    setOpenGroupMenu(!openGroupMenu);
  }
  function closeMenu() {
    setOpenGroupMenu(false);
    setSelectedData({ index: -1, section: Section.groups });
  }
  function closeBrowseMenu() {
    setOpenBrowseMenu(false);
    setSelectedData({ index: -1, section: Section.groups });
  }

  function mouseIn(index: number, section: Section) {
    setHoverData({ index, section });
  }
  function mouseOut(_index: number, section: Section) {
    setHoverData({ index: -1, section });
  }

  async function doGroupRecovery() {
    try {
      if (sending) { return; }
      if (!selectedGroup) { throw new Error("Not Selected"); }
      if (!authContext.membership) { throw new Error("No current membership"); }
      const groupService = groupServiceFactory();

      setSending(true);
      await groupService.reWelcomeGroup(authContext.membership.accountID,
        selectedGroup.groupID);

      setOpenGroupMenu(false);
    } catch (err) {
      if (err instanceof RecoveryTooOldError || err instanceof GroupError && err.code === ErrorCode.sync) {
        setOpenFailedRecovery(true);
      } else {
        setErrMsg((err as Error).message);
      }
    } finally {
      setSending(false);
    }
  }

  async function doMarkGroupAsRead() {
    try {
      if (sending) { return; }
      if (!selectedGroup) { throw new Error("Not Selected"); }
      const groupService = groupServiceFactory();
      
      setSending(true);
      await groupService.markGroupAsRead(selectedGroup);

      setOpenGroupMenu(false);
    } catch (err) {
      setErrMsg((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  async function doMarkAllGroupsAsRead() {
    try {
      if (sending) { return; }
      const groupService = groupServiceFactory();

      setSending(true);
      await bluebird.map(props.groups, group => {
        return group.unreadMentions || group.unreadMessages ?
          groupService.markGroupAsRead(group) :
          bluebird.resolve()
      });
    } catch (err) {
      setErrMsg((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  async function doRemoveDevice() {
    try {
      if (sending) { return; }
      if (!selectedGroup) { throw new Error("No Group Selected!"); }
      if (!authContext.membership) { throw new Error("No current membership"); }

      setSending(true);
      const groupService = groupServiceFactory();
      await groupService.removeDeviceFromGroup(authContext.membership.accountID,
        authContext.membership.userID, selectedGroup.groupID,
        authContext.membership.userID);
      setOpenGroupMenu(false);
      setSelectedData({ index: -1, section: Section.groups });
    } catch (err) {
      console.error("Error removing this device from group");
      setErrMsg((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  /*function doLeaveGroup(event: React.MouseEvent<HTMLLIElement>) {
    event.stopPropagation();
    event.preventDefault();
  }*/

  async function doAddThisDevice() {
    try {
      if (sending) { return; }
      if (!selectedGroup) { throw new Error("No Group Selected!"); }

      setSending(true);
      await navContext.addThisDeviceToGroup(selectedGroup.groupID);
      setOpenGroupMenu(false);
      setSelectedData({ index: -1, section: Section.groups });
    } catch (err) {
      console.error("Error adding this device to group");
      setErrMsg((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  function doSettingsNav() {
    if (!selectedGroup) { return; }
    navContext.navTo(`/group/${selectedGroup.groupID}/settings`);
    setOpenGroupMenu(false);
    setSelectedData({ index: -1, section: Section.groups });
  }

  function doMemberListNav() {
    if (!selectedGroup) { return; }
    navContext.navTo(`/group/${selectedGroup.groupID}/members`);
    setOpenGroupMenu(false);
    setSelectedData({ index: -1, section: Section.groups });
  }

  function doFileListNav() {
    if (!selectedGroup) { return; }
    navContext.navTo(`/group/${selectedGroup.groupID}/files`);
    setOpenGroupMenu(false);
    setSelectedData({ index: -1, section: Section.groups });
  }

  const menuItems: Array<React.ReactElement<any, any>> = [
    <MenuItem key="members" onClick={doMemberListNav} disableRipple disableTouchRipple>
      <ListItemIcon><PeopleAltIcon /></ListItemIcon>
      <ListItemText>Members</ListItemText>
    </MenuItem>,
    <MenuItem key="files" onClick={doFileListNav} disableRipple disableTouchRipple>
      <ListItemIcon><FolderOpenIcon /></ListItemIcon>
      <ListItemText>Files</ListItemText>
    </MenuItem>
  ];
  if (selectedGroup) {
    if (selectedGroup.grouptype !== GroupType.dm ||
      (authContext.membership && authContext.membership.canArchive && 
        !authContext.membership?.mustArchive)) {
      menuItems.unshift(
        <MenuItem key="settings" onClick={doSettingsNav} disableRipple disableTouchRipple>
          <ListItemIcon><SettingsIcon /></ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>);
    }
    if (selectedGroup.outOfSync) {
      menuItems.push(<Divider key="sync-divider" />);
      menuItems.push(
        <MenuItem key="recover" onClick={doGroupRecovery}>Attempt Recovery</MenuItem>);
    }

    if (selectedGroup.unreadMentions || selectedGroup.unreadMessages) {
      menuItems.push(<Divider key="read-divider" />);
      menuItems.push(
        <MenuItem key="mark-read" onClick={doMarkGroupAsRead}>
          <ListItemIcon><MarkEmailReadIcon /></ListItemIcon>
          <ListItemText>Mark Group as Read</ListItemText>
        </MenuItem>);
    }

    if (selectedGroup.haveRatchetTree && selectedGroup.fullyWelcomed) {
      menuItems.push(<Divider key="device-divider" />);
      menuItems.push(
        <MenuItem key="remove" onClick={doRemoveDevice}
          disableRipple disableTouchRipple>
          <ListItemIcon><LogoutIcon /></ListItemIcon>
          <ListItemText>Remove This Device</ListItemText>
        </MenuItem>);
      //menuItems.push(
      //<MenuItem key="leave" onClick={doLeaveGroup}>Leave Group</MenuItem>);
    } else if (!selectedGroup.haveRatchetTree) {
      menuItems.push(<Divider key="device-divider" />);
      menuItems.push(
        <MenuItem key="join" onClick={doAddThisDevice}>Add This Device</MenuItem>);
    }
  }

  function renderThreadsLink(): React.ReactElement<any, any> {
    let classes = "group-list-item";
    if (props.unreadThreadMentions > 0) {
      classes += " unread-mentions";
    }
    if (props.unreadThreadMessages > 0) {
      classes += " unread-messages";
    }
    if (selectedData.index === 1 && selectedData.section === Section.headers) {
      classes += " current";
    }

    return <div className={classes} onClick={threadsClick}>
      <div className="left-indicator"></div>
      <Typography variant="h3" className="group-name" display="inline">
        <span>Threads</span>
      </Typography>
    </div>;
  }
  function renderMembership(groupmember: GroupMembership, index: number, section: Section) {
    let classes = "group-list-item";

    // if menu is showing, only hover that group item, otherwise hover
    if (openGroupMenu && selectedData.index === index && selectedData.section === section) {
      classes += " hover";
    } else if (!openGroupMenu && hoverData.index === index && hoverData.section === section) {
      classes += " hover";
    }
    if (!groupmember.haveRatchetTree) {
      classes += " missingRatchet";
    }
    if (groupmember.outOfSync) {
      classes += " out-of-sync";
    }
    if (groupmember.unreadMessages) {
      classes += " unread-messages";
    }
    if (groupmember.unreadMentions) {
      classes += " unread-mentions";
    }
    if (groupmember.ongoingHuddle) {
      classes += " with-huddle";
    }
    if (groupmember.groupID === props.currentGroup?.groupID) {
      classes += " current";
    }
    return <div
      className={classes} key={groupmember.groupID}
      data-group-id={groupmember.groupID}
      title={groupmember.groupname}
      onClick={e => groupClick(e, groupmember, index, section)}
      onMouseEnter={() => mouseIn(index, section)}
      onMouseLeave={() => mouseOut(index, section)}>
      <div className="left-indicator">
        <Tooltip title="This device is out of sync with the group" arrow>
          <SyncProblemIcon className="sync-problem-indicator" />
        </Tooltip>
        <Tooltip title="This device has not been added to this group" arrow>
          <i className="fa-solid fa-users-slash device-not-in-group" />
        </Tooltip>
      </div>
      <Typography variant="h3" className="group-name" display="inline">
        <span>{groupmember.groupname}</span>
      </Typography>
      <span className="right-controls">
        <IconButton size="small" className="huddle-indicator" disableRipple disableTouchRipple
          onClick={() => toggleHuddle(groupmember.groupID)}>
          <Tooltip title="This group is in a huddle" arrow>
            <i className="fa-solid fa-video" />
          </Tooltip>
        </IconButton>
        <div className="mention-count">
          <span>&nbsp;</span>
        </div>
        <div className="spacer" />
        <IconButton className="group-actions" size="small"
          disableRipple disableTouchRipple
          aria-label="group-info-menu" aria-haspopup="true"
          onClick={e => doOpenMenu(e, groupmember, index, section)}>
          <MoreVertIcon />
        </IconButton>
      </span>
    </div>;
  }

  return <nav className="groups-list-container">
    <section className="other-nav-section">
      {renderThreadsLink()}
      <div className="group-list-item" onClick={() => props.onOpenEditMeetingDialog()}>
        <div className="left-indicator"></div>
        <Typography variant="h3" className="group-name" display="inline">
          <span>Create Meeting</span>
        </Typography>
      </div>
    </section>
    <section className="public-section">
      <header>
        <h2 onClick={() => authContext.can(PermissionLevel.WRITE, "groups") && navContext.navTo("/group/browse")}>
          Groups
        </h2>
        {authContext.can(PermissionLevel.WRITE, "groups") &&
          <IconButton className="add-group-btn" size="small"
            aria-label="groups-menu"
            disableRipple disableTouchRipple
            ref={browseMenuAnchorRef}
            onClick={() => setOpenBrowseMenu(true)}>
            <ExpandMoreIcon />
          </IconButton>}
      </header>
      {props.groups.map((member, i) => renderMembership(member, i, Section.groups))}
    </section>
    <section>
      <header>
        <h2>Direct Messages</h2>
        {authContext.can(PermissionLevel.WRITE, "groups") &&
          <IconButton className="add-dm-btn" size="small"
            disableRipple disableTouchRipple
            onClick={() => navContext.navTo("/group/newDM")}>
            <AddIcon />
          </IconButton>}
      </header>
      {props.directMessages.map((member, i) => renderMembership(member, i, Section.directMessages))}
    </section>
    <Popper
      className="popup-menu"
      open={openBrowseMenu}
      anchorEl={browseMenuAnchorRef.current}
      disablePortal={true} placement="bottom-end">
      <Paper>
        <ClickAwayListener onClickAway={closeBrowseMenu}>
          <MenuList id="groups-menu">
            <MenuItem
              disableRipple disableTouchRipple
              onClick={() => { closeBrowseMenu(); navContext.navTo("/group/browse"); }}>
              <ListItemIcon><GroupsIcon /></ListItemIcon>
              <ListItemText>Browse Groups</ListItemText>
            </MenuItem>
            <MenuItem
              disableRipple disableTouchRipple
              onClick={() => { closeBrowseMenu(); props.onDoAddGroup(); }}>
              <ListItemIcon><AddIcon /></ListItemIcon>
              <ListItemText>New Group</ListItemText>
            </MenuItem>
            {checkAnyUnread() && <div>
              <Divider key="all-read-divider" />
              <MenuItem
                disableRipple disableTouchRipple
                onClick={() => { closeBrowseMenu(); doMarkAllGroupsAsRead(); }}>
                <ListItemIcon><MarkEmailReadIcon /></ListItemIcon>
                <ListItemText>Mark All Groups as Read</ListItemText>
              </MenuItem></div>}
          </MenuList>
        </ClickAwayListener>
      </Paper>
    </Popper>
    <Popper
      className="popup-menu"
      open={openGroupMenu}
      anchorEl={anchorEl}
      disablePortal={true}
      placement="bottom-end">
      <Paper>
        <ClickAwayListener onClickAway={closeMenu}>
          <MenuList id="group-meta-menu">
            {menuItems}
          </MenuList>
        </ClickAwayListener>
      </Paper>
    </Popper>
    <FailedRecoveryDialog open={openFailedRecovery}
      groupID={selectedGroup?.groupID || ""}
      onClose={() => setOpenFailedRecovery(false)} />
    <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("")} />
  </nav>;
}
